import type { AdminCountryDto } from "@/lib/api/types";

/** Normalize legacy / DB spacing so comparisons match dropdown values. */
export function normalizeCountryField(s: string | null | undefined): string {
  return (s ?? "")
    .replace(/\u00a0/g, " ")
    .replace(/\u200b/g, "")
    .trim();
}

/** Value stored on the member `region` field (matches `banks.region` when countries are configured that way). */
export function memberRegionValue(c: AdminCountryDto): string {
  const r = (c.region ?? "").trim();
  if (r.length > 0) return r;
  const n = (c.name ?? "").trim();
  if (n.length > 0) return n;
  return c.id;
}

/** Label shown in country dropdowns. */
export function countryListLabel(c: AdminCountryDto): string {
  const name = (c.name ?? "").trim();
  const reg = (c.region ?? "").trim();
  if (name && reg) return `${name} (${reg})`;
  if (name) return name;
  return c.id;
}

/** Value stored in `addresses.country` (varchar 50, legacy-style display). */
export function addressCountryValue(c: AdminCountryDto): string {
  const n = (c.name ?? "").trim();
  if (n.length > 0) return n.length <= 50 ? n : n.slice(0, 50);
  return memberRegionValue(c).slice(0, 50);
}

export function countryOptionValuesEqual(a: string, b: string): boolean {
  return (
    normalizeCountryField(a).toLowerCase() === normalizeCountryField(b).toLowerCase()
  );
}

/** Canonical value for a controlled region select (matches an option or legacy orphan). */
export function memberRegionSelectValue(
  countries: AdminCountryDto[],
  stored: string | null | undefined
): string {
  const raw = normalizeCountryField(stored);
  if (!raw) return "";
  const hit = countries.find((c) =>
    countryOptionValuesEqual(memberRegionValue(c), raw)
  );
  return hit ? memberRegionValue(hit) : raw;
}

/** Canonical value for a controlled address country select. */
export function addressCountrySelectValue(
  countries: AdminCountryDto[],
  stored: string | null | undefined
): string {
  const raw = normalizeCountryField(stored);
  if (!raw) return "";
  const hit = countries.find((c) =>
    countryOptionValuesEqual(addressCountryValue(c), raw)
  );
  return hit ? addressCountryValue(hit) : raw;
}

/** Resolves `addresses.country` display string to `countries.id` for `/admin/states?countryId=`. */
export function resolveCountryIdFromAddressCountryField(
  countryField: string | null | undefined,
  countries: AdminCountryDto[]
): string | null {
  const raw = normalizeCountryField(countryField);
  if (!raw) return null;

  const byAddressValue = countries.find((c) =>
    countryOptionValuesEqual(addressCountryValue(c), raw)
  );
  if (byAddressValue) return byAddressValue.id;

  const byName = countries.find((c) =>
    countryOptionValuesEqual((c.name ?? "").trim(), raw)
  );
  if (byName) return byName.id;

  const byRegion = countries.find((c) =>
    countryOptionValuesEqual(memberRegionValue(c), raw)
  );
  if (byRegion) return byRegion.id;

  const byLabel = countries.find((c) =>
    countryOptionValuesEqual(countryListLabel(c), raw)
  );
  if (byLabel) return byLabel.id;

  const byId = countries.find((c) => countryOptionValuesEqual(c.id, raw));
  if (byId) return byId.id;

  const codeStr = raw.replace(/\s+/g, "");
  if (/^\d+$/.test(codeStr)) {
    const code = Number.parseInt(codeStr, 10);
    if (!Number.isNaN(code)) {
      const byCode = countries.find((c) => c.code === code);
      if (byCode) return byCode.id;
    }
  }

  return null;
}
